import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Typography,
} from "@mui/material";
import { CustomDialog } from "components/booking-documents/mui.styles";
import { CustomTextField } from "components/mui-common/CustomTextField";
import { REGISTRATION_TYPES } from "constants/general";
import MESSAGES from "constants/messages";
import { ROUTES } from "constants/routes";
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  getAdditionalUserInfo,
  getAuth,
  signInWithPopup,
} from "firebase/auth";

import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import authService from "services/auth.service";
import { signInUserAction, signUpUserAction } from "slices/authReducers";
import { handleSnackBar } from "slices/global";
import { useAppDispatch } from "store/appHooks";
import { FORM_VALIDATIONS } from "constants/formValidation";
import { SocialPill } from "components/login/mui.styles";

const SignInOptions = () => {
  const { control, handleSubmit, setValue } = useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const doPlatformSignIn = (provider: any, providerId: number) => {
    const auth = getAuth();

    signInWithPopup(auth, provider)
      .then(async (result) => {
        const { user }: any = result;
        const { isNewUser }: any = getAdditionalUserInfo(result);

        if (!user?.email) {
          dispatch(
            handleSnackBar({
              message:
                "Some required details we can't be able to fetch, Try again with different account.",
              open: true,
              severity: "error",
            })
          );
          await authService.doLogout();
          return;
        }
        // consider as new user
        if (isNewUser) {
          setValue("registration_type", providerId);
          setValue("email", user.email);
          setValue("name", user?.displayName ?? user.email);
          setValue("firebase_id", user.uid);

          submitForm({
            registration_type: providerId,
            email: user.email,
            name: user?.displayName ?? user.email,
            firebase_id: user.uid,
          });
          setShowConfirmation(false);
          return;
        } else {
          dispatch(
            signInUserAction({
              data: { firebase_id: user.uid, registration_type: providerId },
              navigate: () => navigate(ROUTES.HOME),
            })
          );
        }
      })
      .catch((error) => {
        dispatch(
          handleSnackBar({
            message: error?.message ?? MESSAGES.ERROR,
            open: true,
            severity: "error",
          })
        );
      });
  };

  const signInWith = (type: string) => {
    switch (type) {
      case "GOOGLE":
        doPlatformSignIn(new GoogleAuthProvider(), REGISTRATION_TYPES.GOOGLE);
        break;
      case "FACEBOOK":
        doPlatformSignIn(
          new FacebookAuthProvider(),
          REGISTRATION_TYPES.FACEBOOK
        );
        break;
      case "APPLE":
        doPlatformSignIn(
          new OAuthProvider("apple.com"),
          REGISTRATION_TYPES.APPLE
        );
        break;
    }
  };

  const submitForm = (data: any) => {
    dispatch(
      signUpUserAction({
        data,
        navigate: () => goToLogin(),
      })
    );
  };

  const goToLogin = () => {
    navigate(ROUTES.HOME);
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
      <SocialPill onClick={() => signInWith("APPLE")}>
        <img
          height={20}
          width={20}
          src={require("assets/img/png/social/apple-color.png")}
          alt=""
        />
      </SocialPill>

      <SocialPill onClick={() => signInWith("GOOGLE")}>
        <img
          height={20}
          width={20}
          src={require("assets/img/png/social/google-color.png")}
          alt=""
        />
      </SocialPill>
      <SocialPill onClick={() => signInWith("FACEBOOK")}>
        <img
          height={20}
          width={20}
          src={require("assets/img/png/social/fb_social.png")}
          alt=""
        />
      </SocialPill>

      {showConfirmation && (
        <Dialog open PaperComponent={CustomDialog} fullWidth maxWidth="sm">
          <form onSubmit={handleSubmit(submitForm)}>
            <DialogTitle>
              <Typography fontSize={25} fontWeight={600}>
                Phone Number
              </Typography>
              <Typography fontSize={14} color="#434343" fontWeight={500}>
                We required your phone number for this account.
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box>
                <Controller
                  control={control}
                  name="phone"
                  rules={{
                    ...FORM_VALIDATIONS.REQUIRED_MOBILE,
                    ...FORM_VALIDATIONS.maxLengthValidation(10),
                    ...FORM_VALIDATIONS.minLengthValidation(10),
                  }}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <CustomTextField
                      variant="outlined"
                      placeholder="Phone Number"
                      fullWidth
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error?.message ?? ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">+1</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" type="submit">
                Submit
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </Box>
  );
};

export default SignInOptions;
